/**
 * user-date-time — „data z godziną” po stronie użytkownika: wpis i wyświetlanie na zegarze
 * STREFY PRZEGLĄDARKI, w formacie JĘZYKA APLIKACJI (zasada → FRONTEND.md §3.5).
 *
 * Cienka warstwa nad funkcjami z `@saas/shared` (`time-zone.ts`), które biorą strefę jawnie —
 * tu podstawiamy strefę przeglądarki i język interfejsu, żeby ekrany nie powtarzały tego same.
 * Logika liczona w strefie TENANTA (numeracja, okna dobowe) żyje na serwerze, nie tutaj.
 */

import {
  DEFAULT_TIME_ZONE,
  formatDateTime,
  fromDateTimeInput,
  toDateKey,
  toDateTimeInput,
} from '@saas/shared';
import i18next from 'i18next';

/** Strefa czasowa przeglądarki (IANA); gdy środowisko jej nie zna — strefa domyślna. */
export function userTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TIME_ZONE;
  } catch {
    return DEFAULT_TIME_ZONE;
  }
}

/** Kod formatu daty dla języka aplikacji: polski → `pl-PL`, angielski → `en-GB`. */
export function userDateLocale(language: string | undefined = i18next.language): string {
  return language?.toLowerCase().startsWith('en') ? 'en-GB' : 'pl-PL';
}

/** Moment z API → wartość kontrolki `datetime-local` na zegarze przeglądarki. */
export function toUserDateTimeInput(value: unknown): string {
  return toDateTimeInput(value, userTimeZone());
}

/** Wartość kontrolki `datetime-local` (zegar przeglądarki) → moment ISO w UTC albo `null`. */
export function fromUserDateTimeInput(local: string | null | undefined): string | null {
  return fromDateTimeInput(local, userTimeZone());
}

/** Data z godziną do wyświetlenia: zegar przeglądarki, format języka aplikacji. */
export function formatUserDateTime(
  value: unknown,
  options: Intl.DateTimeFormatOptions = {},
): string {
  return formatDateTime(value, userTimeZone(), userDateLocale(), options);
}

/** Dzień `YYYY-MM-DD` wpisu w kalendarzu przeglądarki (data bez godziny — bez zmian). */
export function toUserDateKey(value: unknown): string | null {
  return toDateKey(value, userTimeZone());
}
